import React from "react";
import { FaCar } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { resolveBackendUrl } from "../../utils/accountAvatarUrl";
import { formatWeekDayHeader } from "../../utils/timesheetWeekRows";
import {
  shiftRowHasTravel,
  travelPayRateSuffix,
} from "../../utils/shiftTravelDisplay";
import "./TimesheetWeekView.css";

function WeekDayHeader({ dateStr }) {
  const { dayOfWeek, dayLabel } = formatWeekDayHeader(dateStr);
  return (
    <div className="week-day-header">
      <b>{dayOfWeek}</b>
      <span>{dayLabel}</span>
    </div>
  );
}

function formatDayCellValue(times) {
  if (!times?.length) return "";
  return times.join(", ");
}

const emptyShiftEntry = { in: "", out: "", break: "" };

function getCellEntries(cell, targetCount = 1) {
  const entries = cell?.entries?.length
    ? cell.entries
    : Array.from(
        {
          length: Math.max(
            cell?.in?.length || 0,
            cell?.out?.length || 0,
            cell?.break?.length || 0,
          ),
        },
        (_, idx) => ({
          in: cell?.in?.[idx] || "",
          out: cell?.out?.[idx] || "",
          break: cell?.break?.[idx] || "",
        }),
      );

  const padded = entries.length ? [...entries] : [];
  while (padded.length < targetCount) {
    padded.push(emptyShiftEntry);
  }
  return padded;
}

function getRowShiftCount(row, weekDateRange) {
  return Math.max(
    1,
    ...weekDateRange.map((dateStr) => {
      const cell = row.byDate?.get(dateStr);
      return Math.max(
        cell?.entries?.length || 0,
        cell?.in?.length || 0,
        cell?.out?.length || 0,
        cell?.break?.length || 0,
      );
    }),
  );
}

/**
 * Legacy-style week sign-in sheet: document header + staff rows × day columns.
 */
export default function TimesheetWeekView({
  weekDateRange = [],
  staffRows = [],
  locationLabel = "",
  /** Client location id for the QR `locationId` param. */
  qrLocationId = "",
  clientName = "",
  clientAvatarSrc = "",
  clientInitials = "CL",
  dateRangeLabel = "",
  supervisorsLabel = "",
  staffRequestIds = "",
  positionTitle = "",
  talentLogoSrc,
  filterClient,
  baseDateHdr,
  rangeDaysHdr,
  endDateIso,
  clientSearchName,
  AvatarSmall,
  printWithoutValues = false,
}) {
  const resolvedClientAvatar = resolveBackendUrl(clientAvatarSrc);

  const buildQrHref = () => {
    const params = new URLSearchParams();
    if (filterClient) params.set("clientId", String(filterClient));
    if (baseDateHdr) params.set("from", baseDateHdr);
    if (rangeDaysHdr > 1 && endDateIso) params.set("to", endDateIso);
    if (clientSearchName) params.set("client", clientSearchName);
    if (locationLabel) params.set("location", locationLabel);
    if (qrLocationId) params.set("locationId", String(qrLocationId));
    params.set("forceLogin", "1");
    return `/checkin${params.toString() ? `?${params.toString()}` : ""}`;
  };

  const qrValue = `${typeof window !== "undefined" ? window.location.origin : ""}${buildQrHref()}`;

  return (
    <div className="timesheet-week-view">
      <div className="timesheet-week-view__header">
        <div className="timesheet-week-view__header-logo flex-shrink-0">
          {talentLogoSrc ? (
            <img src={talentLogoSrc} alt="Talent Logo" />
          ) : null}
        </div>

        <div className="timesheet-week-view__header-center">
          <div className="timesheet-label">Timesheet</div>
          <div className="timesheet-range">{dateRangeLabel}</div>
        </div>

        <div className="timesheet-week-view__header-client">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#DC1F00] text-white font-semibold text-lg overflow-hidden border border-gray-200">
            {resolvedClientAvatar ? (
              <>
                <img
                  src={resolvedClientAvatar}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fb = e.currentTarget.nextElementSibling;
                    if (fb) fb.style.display = "flex";
                  }}
                />
                <span
                  className="flex h-full w-full items-center justify-center"
                  style={{ display: "none" }}
                >
                  {clientInitials}
                </span>
              </>
            ) : (
              <span>{clientInitials}</span>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 text-lg">{clientName || "Client"}</div>
            {locationLabel ? (
              <div className="text-gray-700 text-sm font-semibold mt-0.5 uppercase tracking-wide">
                {locationLabel}
              </div>
            ) : null}
            {/* Position label hidden per client request (weekly timesheet view) — still
                computed/passed through in case other logic depends on it, just not
                rendered. Can be re-enabled by dropping the `false &&` below. */}
            {false && positionTitle ? (
              <div className="text-gray-600 text-sm mt-1">{positionTitle}</div>
            ) : null}
            <div className="text-gray-600 text-xs mt-2">
              Supervisor(s):{" "}
              <span className="font-medium">{supervisorsLabel || "\u2014"}</span>
            </div>
            {staffRequestIds ? (
              <div className="text-gray-600 text-xs mt-1">
                Staff Request:{" "}
                <span className="font-medium">{staffRequestIds}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex-shrink-0">
          <a
            href={buildQrHref()}
            target="_blank"
            rel="noopener noreferrer"
            className="h-20 w-20 bg-white rounded border border-gray-200 flex items-center justify-center p-1.5 hover:border-[#DC1F00] transition-colors cursor-pointer"
            aria-label="QR Check-in"
          >
            <QRCodeSVG value={qrValue} size={68} level="M" />
          </a>
        </div>
      </div>

      <div className="timesheet-week-view__table-wrap">
        <table className="timesheet-week-table">
          <thead>
            <tr>
              <th className="staff-col" />
              {weekDateRange.map((dateStr) => (
                <th key={`week-day-h-${dateStr}`}>
                  <WeekDayHeader dateStr={dateStr} />
                </th>
              ))}
              <th className="total-week-col">
                <div className="week-day-header">
                  <b>Total</b>
                  <span>Week</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {staffRows.length === 0 ? (
              <tr>
                <td
                  colSpan={weekDateRange.length + 2}
                  className="px-3 py-6 text-center text-muted-foreground text-sm"
                >
                  No scheduled shifts in this range for this location.
                </td>
              </tr>
            ) : (
              staffRows.map((row, idx) => {
                const rowShiftCount = getRowShiftCount(row, weekDateRange);
                return (
                <tr key={`week-staff-${idx}`} className="common-row">
                  <td className="staff-cell">
                    <div className="staff-cell-inner">
                      <div className="staff-cell-account">
                        {AvatarSmall ? (
                          <AvatarSmall
                            name={row.staffName}
                            src={row.avatarUrl}
                          />
                        ) : null}
                        <div className="flex flex-col min-w-0">
                          <span className="role-name">
                            {row.displayRoleName || row.roleName}
                          </span>
                          <span className="staff-name flex items-center gap-1">
                            {row.staffName}
                            {shiftRowHasTravel(
                              row.travelTimeAmount,
                              row.travelTimeType,
                            ) && (
                              <span
                                className="inline-flex items-center"
                                title={travelPayRateSuffix(
                                  row.travelTimeType,
                                  row.travelTimeAmount,
                                )}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 16 16"
                                  className="w-4 h-4 text-yellow-600"
                                  aria-hidden="true"
                                >
                                  <g transform="rotate(45 8 8)">
                                    <rect
                                      x="2"
                                      y="2"
                                      width="12"
                                      height="12"
                                      rx="1"
                                      fill="#b5851d"
                                    />
                                    <path
                                      d="M6 8h4M8 6l2 2-2 2"
                                      fill="none"
                                      stroke="#ffffff"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </g>
                                </svg>
                              </span>
                            )}
                          </span>
                          {row.carpoolName && (
                            <span className="flex items-start gap-1">
                              <FaCar
                                className="text-red-600 shrink-0 mt-0.5"
                                size={10}
                              />
                              <span className="min-w-0 flex-1 text-left text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
                                {row.carpoolName}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="staff-cell-labels">
                        {Array.from({ length: rowShiftCount }).map((_, shiftIdx) => (
                          <div
                            key={`week-label-${idx}-${shiftIdx}`}
                            className="label-pair"
                          >
                            <div className="label-cell">IN</div>
                            <div className="label-cell">OUT</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                  {weekDateRange.map((dateStr) => {
                    const cell = row.byDate?.get(dateStr);
                    const entries = getCellEntries(cell, rowShiftCount);
                    return (
                      <td key={`week-day-${idx}-${dateStr}`}>
                        <div className="week-shift-list">
                          {entries.map((entry, shiftIdx) => {
                            const inVal = printWithoutValues
                              ? ""
                              : formatDayCellValue([entry.in].filter(Boolean));
                            const outVal = printWithoutValues
                              ? ""
                              : formatDayCellValue([entry.out].filter(Boolean));
                            const breakVal = printWithoutValues
                              ? ""
                              : formatDayCellValue([entry.break].filter(Boolean));

                            return (
                              <div
                                key={`week-day-${idx}-${dateStr}-${shiftIdx}`}
                                className="week-day-entry"
                              >
                                <div className="day-cells">
                                  <div className="time-cell">{inVal || "\u00a0"}</div>
                                  <div className="time-cell">{outVal || "\u00a0"}</div>
                                </div>
                                <div className="break-minutes-cell" title="Break minutes">
                                  {breakVal || "\u00a0"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                  <td className="total-week-col">
                    <div className="week-shift-list">
                      {Array.from({ length: rowShiftCount }).map((_, shiftIdx) => (
                        <div
                          key={`week-total-${idx}-${shiftIdx}`}
                          className="week-day-entry"
                        >
                          <div className="day-cells">
                            <div className="time-cell">{"\u00a0"}</div>
                            <div className="time-cell">{"\u00a0"}</div>
                          </div>
                          <div className="break-minutes-cell">{"\u00a0"}</div>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="timesheet-week-view__signature">
        <div className="flex items-center text-sm text-gray-700">
          <span className="whitespace-nowrap">Client Signature</span>
          <div className="flex-1 border-b border-gray-700 ml-2" />
        </div>
        <div className="flex items-center text-sm text-gray-700">
          <span className="whitespace-nowrap">Client Printed Name</span>
          <div className="flex-1 border-b border-gray-700 ml-2" />
        </div>
        <div className="flex items-center text-sm text-gray-700">
          <span className="whitespace-nowrap">Total Hours</span>
          <div className="flex-1 border-b border-gray-700 ml-2" />
        </div>
      </div>
    </div>
  );
}
